import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useSelector } from "react-redux";

import { getSampleByContext, sampleByContext } from '../../data/sampleRoachData';
import CampusHeatmap from './CampusHeatmap';
import AILightComponent from "../../../../components/AILightComponent";

const CampusPulse = ({ pulseContext = "Campus", campusId = "C0000000" }) => {
  const authState = useSelector((state) => state.authReducer);
  const [timeRange, setTimeRange] = useState('30d');
  const [useSample, setUseSample] = useState(true); 
  const [campusData, setCampusData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showHeatmap, setShowHeatmap] = useState(false);

  const [enableGenAIHelp, setEnableGenAIHelp] = useState(false);
  const toggleGenAIHelp = () => setEnableGenAIHelp((p) => !p);

  const hasFetchedRef = useRef(false);

  // Simulated real data fetch (replace with your actual API call)
  const loadCampusPulse = async () => {
    if (useSample) {
      setCampusData(sampleByContext.Campus || getSampleByContext("Campus"));
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const config = {
        url: "/pd/roach/campus-pulse",
        method: "post",
        data: { 
          campusId, 
          pulseContext: "Campus",
          timeRange 
        },
        headers: { Authorization: authState.token },
      };
      // const res = await fetchData(config);
      // setCampusData(res?.data || []);
      
      // Fallback to sample for now
      setCampusData(sampleByContext.Campus);
    } catch (err) {
      console.error("Failed to load campus pulse", err);
      setCampusData(sampleByContext.Campus);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!hasFetchedRef.current) {
      loadCampusPulse();
      hasFetchedRef.current = true;
    }
  }, [campusId, timeRange, useSample]);

  const data = useMemo(() => {
    return campusData.length > 0 ? campusData : getSampleByContext("Campus");
  }, [campusData]);

  // Aggregate Campus Health Score
  const campusHealth = useMemo(() => {
    if (!data.length) return { avg: 0, trend: 'stable', count: 0 };

    const total = data.reduce((sum, r) => sum + r.avgScore, 0);
    const avg = parseFloat((total / data.length).toFixed(1));
    
    const recent = [...data]
      .sort((a, b) => new Date(b.roachDate) - new Date(a.roachDate))
      .slice(0, Math.floor(data.length * 0.4));

    const oldAvg = data.length > recent.length 
      ? data.slice(recent.length).reduce((sum, r) => sum + r.avgScore, 0) / (data.length - recent.length)
      : avg;

    const trend = avg > oldAvg + 0.5 ? 'up' : avg < oldAvg - 0.5 ? 'down' : 'stable';

    return { avg, trend, count: data.length, recentCount: recent.length };
  }, [data]);

  // Dimension Breakdown (7 Survival Levers)
  const dimensionBreakdown = useMemo(() => {
    const dimensions = [
      { key: 0, label: "🌪 Chaos Level", emoji: "🌪️" },
      { key: 1, label: "🔥 Heat Exposure", emoji: "🔥" },
      { key: 2, label: "🎭 Humor Shield", emoji: "🎭" },
      { key: 3, label: "🧊 Numb Mode", emoji: "🧊" },
      { key: 4, label: "⚡ Focus Spark", emoji: "⚡" },
      { key: 5, label: "📱 Doom Scroll Damage", emoji: "📱" },
      { key: 6, label: "🧠 Recovery Speed", emoji: "🧠" },
    ];

    return dimensions.map(dim => {
      const values = data.flatMap(r => {
        const levers = r.levers ? r.levers.split(',').map(Number) : [];
        return levers[dim.key] || 0;
      });
      
      const avg = values.length 
        ? parseFloat((values.reduce((a, b) => a + b, 0) / values.length).toFixed(1))
        : 0;

      return { ...dim, avg };
    });
  }, [data]);

  // Trend Calculation (same logic as DriftIntelligence)
  const calculateTrend = (dataset) => {
    if (dataset.length < 3) return { slope: 0, direction: 'stable', strength: 0 };

    const sorted = [...dataset].sort((a, b) => new Date(a.roachDate) - new Date(b.roachDate));
    const n = sorted.length;
    const x = Array.from({ length: n }, (_, i) => i);
    const y = sorted.map(r => r.avgScore);

    const sumX = x.reduce((a, b) => a + b, 0);
    const sumY = y.reduce((a, b) => a + b, 0);
    const sumXY = x.reduce((sum, xi, i) => sum + xi * y[i], 0);
    const sumX2 = x.reduce((sum, xi) => sum + xi * xi, 0);

    const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);

    let direction = 'stable';
    if (slope > 0.15) direction = 'up';
    else if (slope < -0.15) direction = 'down';

    return {
      slope: parseFloat(slope.toFixed(2)),
      direction,
      strength: Math.min(Math.abs(slope) * 2.5, 1)
    };
  };

  const campusTrend = useMemo(() => calculateTrend(data), [data]);

  // Dominant Campus Archetype
  const dominantArchetype = useMemo(() => {
    const count = {};
    data.forEach(r => {
      count[r.type] = (count[r.type] || 0) + 1;
    });
    return Object.entries(count).sort((a, b) => b[1] - a[1])[0]?.[0] || "Balanced Survivor";
  }, [data]);

  // Intelligence Flags for Management
  const managementFlags = useMemo(() => {
    const flags = [];

    if (campusTrend.direction === 'down' && campusTrend.strength > 0.6) {
      flags.push("⚠️ Campus-wide decline detected — recovery rituals recommended");
    }
    if (campusHealth.avg <= 5.5) {
      flags.push("🔴 Critical campus health — high risk of Numb Mode across students");
    }
    if (dimensionBreakdown[5].avg >= 7) { // Doom Scroll
      flags.push("📱 Doom scrolling spike — digital wellness intervention needed");
    }
    if (dimensionBreakdown[4].avg <= 4) { // Focus
      flags.push("⚡ Focus Spark critically low — academic performance at risk");
    }
    if (campusTrend.direction === 'up' && campusTrend.strength > 0.7) {
      flags.push("🚀 Strong campus momentum — celebrate and reinforce positive behaviors");
    }

    return flags.length > 0 ? flags : ["🌟 Campus pulse is stable — continue current initiatives"];
  }, [campusTrend, campusHealth, dimensionBreakdown]);

  // Recent Activity
  const recentActivity = useMemo(() => {
    return [...data]
      .sort((a, b) => new Date(b.roachDate) - new Date(a.roachDate))
      .slice(0, 10);
  }, [data]);

  return (
    <div className="min-h-screen bg-[#0a0f1c] text-gray-100 pb-8 border-t-2 border-blue-500 py-2 rounded-xl">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-[#0a0f1c] border-b border-gray-800 py-1 px-4">
        <div className="flex flex-col lg:flex-row items-start justify-between mb-2">
            <div className="flex items-center gap-3">
                {/* <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-fuchsia-500 rounded-2xl flex items-center justify-center text-2xl flex-shrink-0">
                    🏛️
                </div> */}
                
                <div className="flex flex-col">
                    <h1 className="text-lg lg:text-xl font-bold tracking-tight text-white">🏛️ Campus Pulse</h1>
                    <p className="text-gray-400 text-xs lg:text-sm">Real-time Behavioral Telemetry • Campus {campusId}</p>
                </div>
            </div>

          <div className="flex items-center gap-3">
            <button
                onClick={() => setShowHeatmap(true)}
                className="flex items-center gap-2 bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500 
                        px-3 py-2 rounded-lg text-xs lg:text-sm font-medium transition-all active:scale-95 h-9"
            >
                🔥 Heatmap
                <span className="text-sm lg:text-lg">📊</span>
            </button>

            <select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
                className="bg-[#111827] border border-gray-700 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-violet-500 h-9"
            >
                <option value="7d">Last 7 Days</option>
                <option value="14d">Last 14 Days</option>
                <option value="30d">Last 30 Days</option>
            </select>

            <div className={`px-4 py-2 rounded-2xl text-sm font-medium flex items-center gap-2 h-9
                ${campusHealth.trend === 'up' ? 'bg-emerald-500/20 text-emerald-400' : 
                campusHealth.trend === 'down' ? 'bg-rose-500/20 text-rose-400' : 'bg-gray-700 text-gray-200'}`}>
                {campusHealth.trend === 'up' ? '📈' : campusHealth.trend === 'down' ? '📉' : '➡️'} 
                {campusHealth.avg}
            </div>
            </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 space-y-8 mt-6">
        {/* Campus Health Overview */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="bg-[#111827] border border-gray-800 rounded-2xl p-6">
            <p className="text-xs uppercase tracking-widest text-gray-200">CAMPUS HEALTH SCORE</p>
            <div className="text-xl lg:text-2xl font-bold mt-3 tabular-nums text-violet-400">
              {campusHealth.avg}
            </div>
            <p className="text-sm text-gray-200 mt-1">from {campusHealth.count} pulses</p>
          </div>

          <div className="bg-[#111827] border border-gray-800 rounded-2xl p-6">
            <p className="text-xs uppercase tracking-widest text-gray-200">TREND</p>
            <div className="flex items-end gap-4 mt-4">
              <span className="text=xl lg:text-2xl">
                {campusTrend.direction === 'up' ? '📈' : campusTrend.direction === 'down' ? '📉' : '➡️'}
              </span>
              <div>
                <div className="tetx-lg lg:text-xl font-bold">+{campusTrend.slope}</div>
                <p className="text-sm text-gray-200">14-day slope</p>
              </div>
            </div>
          </div>

          <div className="bg-[#111827] border border-gray-800 rounded-2xl p-6">
            <p className="text-xs uppercase tracking-widest text-gray-200">DOMINANT ARCHETYPE</p>
            <div className="text-sm lg:text-lg font-semibold text-fuchsia-300 mt-6 leading-tight">
              {dominantArchetype}
            </div>
          </div>
        </div>

        {/* Dimension Radar / Breakdown */}
        <div className="bg-[#111827] border border-gray-800 rounded-2xl p-6">
          <h3 className="text-sm lg:text-lg text-cyan-200 font-semibold">
            📊 7-Dimensional Survival Pulse
          </h3>
          <div className="grid grid-cols-2 lg:grid-cols-7 gap-4">
            {dimensionBreakdown.map((dim, i) => (
              <div key={i} className="bg-[#0a0f1c] rounded-2xl p-4 text-center border border-gray-700 hover:border-violet-500 transition-colors">
                <div className="text-sm lg:text-lg mb-2">{dim.emoji}</div>
                <div className="font-mono text-lg lg:text-xl font-bold text-violet-300">{dim.avg}</div>
                <div className="text-xs text-gray-200 mt-1 line-clamp-2">{dim.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Intelligence Flags */}
        <div className="bg-gradient-to-br from-[#1a2338] to-[#111827] border border-amber-900/50 rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-5">
            <span className="tetx-xl lg:text-2xl">🧠</span>
            <h3 className="text-sm lg:text-lg text-cyan-200 font-semibold">Campus Intelligence Signals</h3>
          </div>
          <div className="space-y-3">
            {managementFlags.map((flag, i) => (
              <div key={i} className="bg-black/60 border-l-4 border-amber-500 rounded-2xl p-5 text-gray-100">
                {flag}
              </div>
            ))}
          </div>
        </div>

        {/* Recent Campus Activity */}
        <div className="bg-[#111827] border border-gray-800 rounded-2xl p-6">
          <h3 className="text-sm lg:text-lg text-cyan-200 font-semibold">Recent Campus Pulses</h3>
          <div className="space-y-1">
            {recentActivity.map((roach, idx) => {
              const isHigh = roach.avgScore >= 7;
              const isLow = roach.avgScore <= 4;
              return (
                <div key={idx} className="flex items-center justify-between bg-[#0a0f1c] hover:bg-[#1a2338] p-4 rounded-2xl border border-transparent hover:border-gray-700 transition-all">
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-2xl flex items-center justify-center text-2xl ${isHigh ? 'bg-emerald-500/20' : isLow ? 'bg-rose-500/20' : 'bg-cyan-500/20'}`}>
                      {isHigh ? '⚡' : isLow ? '🧊' : '🪳'}
                    </div>
                    <div>
                      <div className="font-medium">{roach.type}</div>
                      <div className="text-xs text-gray-200">
                        {new Date(roach.roachDate).toLocaleDateString('en-US', { 
                          month: 'short', 
                          day: 'numeric' 
                        })} • Day {roach.dayNumber}
                      </div>
                    </div>
                  </div>
                  <div className={`text-lg lg:text-xl font-bold tabular-nums ${isHigh ? 'text-emerald-400' : isLow ? 'text-rose-400' : 'text-cyan-400'}`}>
                    {roach.avgScore}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {showHeatmap && (
        <CampusHeatmap 
          data={data} 
          campusId={campusId} 
          onClose={() => setShowHeatmap(false)} 
        />
      )}

      {/* ── AI Coach ── */}
      <div className="flex flex-col w-full gap-2">
        <button
          onClick={toggleGenAIHelp}
          className={`w-fit px-2 py-1 rounded-sm text-[10px] font-bold transition-all ${
            enableGenAIHelp
              ? "bg-purple-500 text-white"
              : "bg-[#111827] text-purple-300 border border-purple-500/30"
          }`}
        >
          {enableGenAIHelp ? "🤖 AI Coach Active" : "🚀 Ask GenAI Coach"}
        </button>

        {enableGenAIHelp && (
          <div className="w-full bg-[#111827] rounded-2xl border border-purple-500/20 p-2">
            <AILightComponent
                mode=""
                param1={`${pulseContext} Pulse Analyst / Head / Observer`}
                param2={`

                  Context: ${pulseContext}

                  Provide a concise coaching report covering:
                    1. Overall behavioral pattern
                    2. Growth momentum
                    3. Emotional consistency
                    4. Strongest behavioral strengths
                    5. Biggest improvement opportunity
                    6. Burnout, regression or plateau risks
                    7. Identity evolution assessment
                    8. Three practical actions for the next few days
                  Keep the response encouraging, actionable, and easy to understand. Avoid repeating raw statistics unless needed to support a recommendation.
                  `}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default CampusPulse;